import { HttpResponse } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import { City, CityAccount, Year } from '@app/core/models';
import { CityService } from '@app/core/services/city.service';
import { UserService } from '@app/core/services/user.service';
import { ErrorService } from '@app/shared/services/error.service';
import { CityEmailDto, CreateCityEmailDto, DashboardCitiesService } from '@cirklo/client';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class CityStoreService {
    private cityService = inject(CityService);
    private dashboardCitiesService = inject(DashboardCitiesService);
    private userService = inject(UserService);
    private errorService = inject(ErrorService);

    private _refreshCities = new BehaviorSubject<boolean>(false);
    private _cities: Observable<City[]> = this._refreshCities.pipe(
        switchMap(() => this.cityService.cities),
        shareReplay(1),
    );
    cityId = toSignal(this.userService.getUserCityId());

    private cityDataFetched = signal(false);
    currentCity = rxResource({
        stream: () => this.getCurrentCity(),
    });
    cityAccounts = rxResource({
        params: () => ({ shouldRequest: this.cityDataFetched(), cityId: this.currentCity.value()?.id }),
        stream: ({ params }) =>
            params.shouldRequest && params.cityId
                ? this.dashboardCitiesService.listCityAccounts(params.cityId)
                : of([]),
        defaultValue: [],
    });
    cityEmails = rxResource({
        params: () => ({ shouldRequest: this.cityDataFetched(), cityId: this.currentCity.value()?.id }),
        stream: ({ params }) =>
            params.shouldRequest && params.cityId ? this.dashboardCitiesService.listCityEmails(params.cityId) : of([]),
        defaultValue: [],
    });

    constructor() {
        this.errorService.handleResourceError(this.cityAccounts, this.cityEmails);
    }

    refreshCities() {
        this._refreshCities.next(true);
    }

    get cities(): Observable<City[]> {
        return this._cities;
    }

    getFirstYearCurrentCityTemp(): Observable<Year> {
        return of({ year: 2017 });
    }

    addCityAccount(cityId: string, cityAccount: CityAccount): Observable<CityAccount> {
        return this.dashboardCitiesService
            .createCityAccount(cityId, {
                name: cityAccount.name,
                iban: cityAccount.iban,
                enabled: cityAccount.enabled,
            })
            .pipe(
                map((response) => {
                    this.cityAccounts.update((accounts) => [...accounts, response]);
                    return response;
                }),
            );
    }

    updateCityAccount(cityId: string, cityAccount: CityAccount): Observable<CityAccount> {
        // check if anything has changed about the account
        const existingAccount = this.cityAccounts.value().find((account) => account.id === cityAccount.id);
        if (
            existingAccount &&
            existingAccount.name === cityAccount.name &&
            existingAccount.iban === cityAccount.iban &&
            existingAccount.enabled === cityAccount.enabled
        ) {
            return of(existingAccount);
        }
        return this.dashboardCitiesService
            .updateCityAccount(cityId, cityAccount.id, {
                name: cityAccount.name,
                iban: cityAccount.iban,
                enabled: cityAccount.enabled,
            })
            .pipe(
                map((response) => {
                    this.cityAccounts.update((cityAccounts) =>
                        cityAccounts.map((account) => (account.id === response.id ? response : account)),
                    );
                    return response;
                }),
            );
    }

    deleteCityAccount(cityId: string, accountId: string): Observable<HttpResponse<CityAccount>> {
        return this.cityService.deleteCityAccount(cityId, accountId).pipe(
            map((response) => {
                this.cityAccounts.update((cityAccounts) => cityAccounts.filter((val) => val.id !== accountId));
                return response;
            }),
        );
    }

    /**
     * Get the current city of the current agent based on its cityId in AWS
     */
    getCurrentCity(): Observable<City | undefined> {
        return combineLatest([this.userService.getUserCityId(), this._cities]).pipe(
            map(([userCityId, cities]) => cities.find((city) => city.id === userCityId)),
        );
    }

    addCityEmail(cityId: string, cityEmail: CreateCityEmailDto): Observable<CityEmailDto> {
        return this.dashboardCitiesService
            .createCityEmail(cityId, {
                email: cityEmail.email,
                name: cityEmail.name,
                corporate: cityEmail.corporate,
                enabled: cityEmail.enabled,
            })
            .pipe(
                map((response) => {
                    this.cityEmails.update((emails) => [...emails, response]);
                    return response;
                }),
            );
    }

    updateCityEmail(cityId: string, emailId: string, cityEmail: CityEmailDto): Observable<CityEmailDto> {
        // check if anything has changed about the email
        const existingEmail = this.cityEmails.value().find((email) => email.id === emailId);
        if (
            existingEmail &&
            existingEmail.email === cityEmail.email &&
            existingEmail.name === cityEmail.name &&
            existingEmail.corporate === cityEmail.corporate &&
            existingEmail.enabled === cityEmail.enabled
        ) {
            return of(existingEmail);
        }
        return this.dashboardCitiesService
            .updateCityEmail(cityId, emailId, {
                email: cityEmail.email,
                name: cityEmail.name,
                corporate: cityEmail.corporate,
                enabled: cityEmail.enabled,
            })
            .pipe(
                map((response) => {
                    this.cityEmails.update((emails) =>
                        emails.map((email) => (email.id === response.id ? response : email)),
                    );
                    return response;
                }),
            );
    }

    deleteCityEmail(cityId: string, emailId: string): Observable<HttpResponse<CityEmailDto>> {
        return this.cityService.deleteCityEmail(cityId, emailId).pipe(
            map((response) => {
                this.cityEmails.update((cityEmails) => cityEmails.filter((val) => val.id !== emailId));
                return response;
            }),
        );
    }

    fetchCityData() {
        this.cityDataFetched.set(true);
    }

    clearLocalStore() {
        this.cityDataFetched.set(false);
    }
}
